import { AppState } from '@redux/store'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  InsuranceMemberInsurerSelection,
  InsuranceMemberStep,
  InsuranceMemberStepPayload,
  InsuranceMemberStepState,
} from '@typesApp/InsuranceMember'
import { SyncInsuranceListenerCallback, SyncInsuranceListenerList } from '@typesApp/SyncListener'

export type InsuranceMemberState = {
  insuranceMemberStep?: InsuranceMemberStepState
  insuranceMemberInsurerSelection?: InsuranceMemberInsurerSelection
  isInsuranceToggleClicked?: boolean
  isProductQuoteError?: boolean
  isMemberEligibilityCheckInProgress?: boolean
  syncInsuranceListeners?: SyncInsuranceListenerList
}

const initialState: InsuranceMemberState = {}

export const insuranceMemberSlice = createSlice({
  name: 'insuranceMember',
  initialState: initialState,
  reducers: {
    setInsuranceMemberStep(state, action: PayloadAction<InsuranceMemberStepPayload>) {
      state.insuranceMemberStep = action.payload
      // clear insurer selection unless still on insurance form step
      if (action.payload.step !== InsuranceMemberStep.FIND_MEMBER) {
        state.insuranceMemberInsurerSelection = undefined
      }
    },
    resetInsuranceMemberStep(state) {
      state.insuranceMemberStep = undefined
    },
    setInsuranceMemberInsurerSelection(state, action: PayloadAction<InsuranceMemberInsurerSelection>) {
      state.insuranceMemberInsurerSelection = action.payload
    },
    resetInsuranceMemberInsurerSelection(state) {
      state.insuranceMemberInsurerSelection = undefined
    },
    setInsuranceToggleClicked(state, action: PayloadAction<boolean>) {
      state.isInsuranceToggleClicked = action.payload
    },
    resetInsuranceToggleClicked(state) {
      state.isInsuranceToggleClicked = undefined
    },
    addSyncInsuranceListener(state, action: PayloadAction<SyncInsuranceListenerCallback>) {
      if (!state.syncInsuranceListeners) {
        state.syncInsuranceListeners = {
          listeners: [],
        }
      }
      state.syncInsuranceListeners?.listeners?.push(action.payload)
    },
    removeSyncInsuranceListener(state, action: PayloadAction<SyncInsuranceListenerCallback>) {
      if (state.syncInsuranceListeners) {
        const handlers = state.syncInsuranceListeners.listeners ?? []
        const remainingHandlers = handlers.filter(handler => handler !== action.payload)
        state.syncInsuranceListeners.listeners = remainingHandlers
      }
    },
    setMemberEligiblityCheckInProgress(state, action: PayloadAction<boolean>) {
      state.isMemberEligibilityCheckInProgress = action.payload
    },
    setProductQuoteError(state, action: PayloadAction<boolean>) {
      state.isProductQuoteError = action.payload
    },
    resetInsuranceMemberState(state) {
      // always retain the listeners, only commerce F/E can remove them
      return {
        ...initialState,
        syncInsuranceListeners: state.syncInsuranceListeners,
      }
    },
  },
})

export const {
  setInsuranceMemberStep,
  resetInsuranceMemberStep,
  setInsuranceMemberInsurerSelection,
  resetInsuranceMemberInsurerSelection,
  setInsuranceToggleClicked,
  resetInsuranceToggleClicked,
  addSyncInsuranceListener,
  removeSyncInsuranceListener,
  setMemberEligiblityCheckInProgress,
  setProductQuoteError,
  resetInsuranceMemberState,
} = insuranceMemberSlice.actions

export const insuranceMemberStepSelector = (state: AppState): InsuranceMemberStepState | undefined =>
  state.insuranceMember.insuranceMemberStep
export const insuranceMemberInsurerSelectionSelector = (state: AppState): InsuranceMemberInsurerSelection | undefined =>
  state.insuranceMember.insuranceMemberInsurerSelection
export const insuranceToggleClickedSelector = (state: AppState): boolean =>
  state.insuranceMember.isInsuranceToggleClicked === true
export const syncInsuranceListenersSelector = (state: AppState): SyncInsuranceListenerList | undefined =>
  state.insuranceMember.syncInsuranceListeners
export const productQuoteErrorSelector = (state: AppState): boolean =>
  state.insuranceMember.isProductQuoteError === true
export const memberEligibilityCheckInProgressSelector = (state: AppState): boolean =>
  state.insuranceMember.isMemberEligibilityCheckInProgress === true
